import React, { useEffect, useState } from "react";
import axios from "axios";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, IconButton, Snackbar, Tooltip } from "@mui/material";

import { useAppProvider } from "../../Providers/appProvider/AppProvider";
import { useAuth } from "../../Providers/AuthProvider";
import { useFormik } from "formik";
import { useGoogleLogin } from "@react-oauth/google";
import { useSearchParams } from "react-router-dom";

import {
  login,
  updateInstances,
  getUsers,
  authLogin,
  changePassword,
  usChangePassword,
} from "../../api";
import { GlobalActionTypes } from "../../Providers/appProvider/reducers/globalReducer";
import {
  getGitHubUrl,
  generateRandomInt,
  authTokenPayload,
} from "../../config/constant";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { cloudServiceBaseUrl } from "../../config";

function Login() {
  const { dispatch } = useAppProvider();
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const urlRef = searchParams.get("ref");

  const [activeForm, setActiveForm] = useState("login"); // login & reset

  const { refreshUserInfo } = useAuth();

  const [bgImage, setBgImage] = useState(null);
  const [loginState, setLoginState] = useState({
    loading: false,
    error: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "info",
    msg: "",
  });
  const [inputType, setInputType] = useState("password");
  const [confirmInputType, setConfirmInputType] = useState("password");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordChangeForm, setPasswordChangeForm] = useState(false);
  //   const [bgImage, setBgImage] = useState(null);

  const formik = useFormik({
    initialValues: {
      userid: "",
      password: "",
    },
    onSubmit: async () => {
      if (passwordChangeForm) {
        setLoading(true);
        const cp = await changePassword({
          userid: formik.values.userid,
          oldpwd: formik.values.password,
          newpwd: newPassword,
        });

        if (cp?.errcode === 0) {
          loginUser();
        } else {
          setSnackbar({ open: true, severity: "error", msg: cp?.error });
          setLoading(false);
        }
      } else {
        loginUser();
      }
    },
  });

  const resetFormik = useFormik({
    initialValues: {
      userid: "",
      email: "",
      href: window.location.href,
    },
    onSubmit: async () => {
      resetPassword();
    },
  });

  const loginUser = async (type, uid) => {
    // type = 0 - Normal Login
    // type = 1 - Oauth Login
    // uid - only required in case of type = 1

    setLoading(true);

    try {
      const { userid, password } = formik.values;

      let authenticate;

      switch (type) {
        case 0:
          if (passwordChangeForm) {
            dispatch({
              type: GlobalActionTypes.SET_PASSWORD,
              payload: newPassword,
            });

            authenticate = await login(userid, newPassword);
          } else {
            dispatch({
              type: GlobalActionTypes.SET_PASSWORD,
              payload: password,
            });

            authenticate = await login(userid, password);
          }
          break;

        case 1:
          const json = authTokenPayload({
            userid: uid,
          });

          authenticate = await authLogin(json);
          break;

        default:
          if (passwordChangeForm) {
            dispatch({
              type: GlobalActionTypes.SET_PASSWORD,
              payload: newPassword,
            });

            authenticate = await login(userid, newPassword);
          } else {
            dispatch({
              type: GlobalActionTypes.SET_PASSWORD,
              payload: password,
            });

            authenticate = await login(userid, password);
          }
          break;
      }

      if (authenticate?.errcode === 0) {
        const apikey = authenticate?.apikey;

        const userInfo = await refreshUserInfo(uid || userid, apikey);

        if (userInfo?.apps?.length > 0 && newPassword) {
          try {
            const apikeys = await Promise.all(
              userInfo?.apps
                ?.filter((x) => x?.forum_uri)
                .map(async (app) => {
                  try {
                    const uri = app.forum_uri.split(":")?.[0];
                    const data = await usChangePassword(
                      uri,
                      authTokenPayload({
                        userid: uid || userid,
                        newpwd: newPassword,
                        change_at_login: 0,
                      })
                    );
                  } catch (err) {
                    return {};
                  }
                })
            );
          } catch (err) {}
        }

        if (typeof userInfo?.server_details?.[0] === "object") {
          updateInstances(userInfo.server_details, apikey);
        }

        if (urlRef) {
          window.location.assign(urlRef);
        } else {
          window.location.assign(`/`);
        }

        formik.resetForm();
        setNewPassword("");
      } else if (authenticate?.errcode === -2) {
        setSnackbar({
          open: true,
          severity: "info",
          msg: `A Password change was requested for your ID please set a new Password for your account`,
        });

        setPasswordChangeForm(true);
      } else {
        let err;

        switch (authenticate?.error?.[0]) {
          case `user doesn't exist, authentication failed`:
            err = `You have not signed up, Please sign up first.`;
            break;

          default:
            err = authenticate?.error?.[0];
            break;
        }

        setSnackbar({
          open: true,
          severity: "error",
          msg: "Error" + (err ? " - " + err : ""),
        });
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${tokenResponse?.access_token}`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse?.access_token}`,
              Accept: "application/json",
            },
          }
        )
        .then(async (res) => {
          if (res?.data?.picture) {
            sessionStorage.setItem("picture", res?.data?.picture);
          }

          if (res?.data?.email) {
            await loginUser(
              1, // o-auth login
              res?.data?.email
            );
          }
        })
        .catch((err) => console.log(err));
    },
    onError: (tokenResponse) => console.log("error", tokenResponse),
  });

  const signinWithGithub = async (accessCode) => {
    try {
      const res = await axios.post(
        `https://${cloudServiceBaseUrl}/auth/github/get_access_token`,
        {
          code: accessCode,
        }
      );

      if (res?.data?.access_token) {
        // const profile = await axios.get(`https://api.github.com/user`, {
        //   headers: {
        //     Authorization: `Bearer ${res?.data?.access_token}`,
        //   },
        // });

        const emails = await axios.get(`https://api.github.com/user/emails`, {
          headers: {
            Authorization: `Bearer ${res?.data?.access_token}`,
          },
        });

        const primaryEmail = emails?.data?.find(
          (obj) => obj?.primary === true
        )?.email;

        if (primaryEmail) {
          await loginUser(1, primaryEmail);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const resetPassword = async () => {
    setLoading(true);

    const response = await axios.post(
      `https://${cloudServiceBaseUrl}/auth/bangdb/reset_password`,
      { ...resetFormik.values, us: process.env.REACT_APP_USER_API }
    );

    const data = await response.data;

    if (data?.success) {
      setSnackbar({
        open: true,
        msg: "New password is sent to your Email.",
        severity: "success",
      });

      resetFormik.setValues({
        userid: "",
        email: "",
        href: window.location.href,
      });

      setActiveForm("login");
    } else {
      setSnackbar({ open: true, msg: data.errors[0], severity: "error" });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (loginState.error) {
      const timer = setTimeout(() => {
        setLoginState((prev) => ({ ...prev, error: false }));
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [loginState.error]);

  useEffect(() => {
    if (code && state) {
      signinWithGithub(code);
    }
  }, [code, state]);

  //   useEffect(() => {
  //     if (urlRef) {
  //       sessionStorage.setItem("url_ref", urlRef);
  //     }
  //   }, [urlRef]);

  useEffect(() => {
    if (!bgImage) {
      const img = randomImages[generateRandomInt(0, 1)];
      setBgImage(img);
    }
  }, []);

  return (
    <>
      <ErrorBoundary>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() =>
            setSnackbar({ open: false, msg: "", severity: "info" })
          }
        >
          <Alert
            onClose={() =>
              setSnackbar({ open: false, msg: "", severity: "info" })
            }
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>

        {activeForm === "login" && (
          <div className="flex min-h-full flex-1 max-lg:min-h-[80vh]">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
              <div className="mx-auto w-full max-w-sm lg:w-96">
                <div>
                  {/* <img
                className="h-10 w-auto"
                src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                alt="Your Company"
              /> */}
                  <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                    Sign in to BangDB
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    Not a member?{" "}
                    <a
                      href={
                        urlRef ? `/auth/signup?ref=${urlRef}` : "/auth/signup"
                      }
                      className="font-semibold text-indigo-600 hover:text-indigo-500 focus:ring-2 focus:ring-indigo-600"
                      // ref={signinRef}
                    >
                      Sign up
                    </a>
                  </p>
                </div>

                <div className="mt-10">
                  <div>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                      }}
                      className="space-y-6"
                    >
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          User ID
                        </label>
                        <div className="mt-2">
                          <input
                            id="userid"
                            name="userid"
                            type="userid"
                            autoComplete="email"
                            required
                            className="border border-gray-300 block w-full rounded-md py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:ring-inset focus:border-0 focus:outline-indigo-600 sm:text-sm sm:leading-6 outline-none px-2"
                            onChange={formik.handleChange}
                            value={formik.values.userid}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Password
                        </label>
                        <div className="mt-2 relative">
                          <input
                            id="password"
                            name="password"
                            type={inputType}
                            autoComplete="current-password"
                            //   required
                            className="border border-gray-300 block w-full rounded-md py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:ring-inset focus:border-0 focus:outline-indigo-600 sm:text-sm sm:leading-6 outline-none px-2"
                            onChange={formik.handleChange}
                            value={formik.values.password}
                          />

                          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <Tooltip
                              title={
                                inputType === "password"
                                  ? "Show Password"
                                  : "Hide Password"
                              }
                            >
                              <IconButton
                                onClick={() =>
                                  setInputType((prev) =>
                                    prev === "password" ? "text" : "password"
                                  )
                                }
                              >
                                {inputType === "password" ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {passwordChangeForm && (
                        <div>
                          <label
                            htmlFor="new-password"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            New Password
                          </label>
                          <div className="mt-2 relative">
                            <input
                              id="new-password"
                              name="new-password"
                              type={confirmInputType}
                              autoComplete="new-password"
                              //   required
                              className="border border-gray-300 block w-full rounded-md py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:ring-inset focus:border-0 focus:outline-indigo-600 sm:text-sm sm:leading-6 outline-none px-2"
                              onChange={(e) => setNewPassword(e.target.value)}
                              value={newPassword}
                            />

                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                              <Tooltip
                                title={
                                  confirmInputType === "password"
                                    ? "Show Password"
                                    : "Hide Password"
                                }
                              >
                                <IconButton
                                  onClick={() =>
                                    setConfirmInputType((prev) =>
                                      prev === "password" ? "text" : "password"
                                    )
                                  }
                                >
                                  {confirmInputType === "password" ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="text-sm leading-6">
                          <a
                            className="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500"
                            onClick={() => setActiveForm("reset")}
                          >
                            Forgot password?
                          </a>
                        </div>
                      </div>

                      <div>
                        <button
                          disabled={loading}
                          type="submit"
                          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          {loading ? (
                            <svg
                              aria-hidden="true"
                              className="w-8 h-8 text-gray-200 animate-spin fill-blue-600"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                          ) : passwordChangeForm ? (
                            "Change Password"
                          ) : (
                            "Sign in"
                          )}
                        </button>

                        {loginState.error && (
                          <span className="block text-red-600 text-sm mt-2">
                            Incorrect credentials, Could not login.
                          </span>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="mt-10">
                    <div className="relative">
                      <div
                        className="absolute inset-0 flex items-center"
                        aria-hidden="true"
                      >
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm font-medium leading-6">
                        <span className="bg-white px-6 text-gray-900">
                          Or continue with
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <a
                        //   href="#"
                        onClick={() => loginWithGoogle()}
                        className="flex w-full items-center justify-center gap-3 rounded-md bg-white shadow-md border px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0] cursor-pointer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          xmlnsXlink="http://www.w3.org/1999/xlink"
                          width="512"
                          height="512"
                          x="0"
                          y="0"
                          viewBox="0 0 512 512"
                          // style="enable-background:new 0 0 512 512"
                          xmlSpace="preserve"
                          className="w-5 h-5"
                        >
                          <g>
                            <path
                              d="m492.668 211.489-208.84-.01c-9.222 0-16.697 7.474-16.697 16.696v66.715c0 9.22 7.475 16.696 16.696 16.696h117.606c-12.878 33.421-36.914 61.41-67.58 79.194L384 477.589c80.442-46.523 128-128.152 128-219.53 0-13.011-.959-22.312-2.877-32.785-1.458-7.957-8.366-13.785-16.455-13.785z"
                              // style=""
                              fill="#167ee6"
                              data-original="#167ee6"
                              // class=""
                            ></path>
                            <path
                              d="M256 411.826c-57.554 0-107.798-31.446-134.783-77.979l-86.806 50.034C78.586 460.443 161.34 512 256 512c46.437 0 90.254-12.503 128-34.292v-.119l-50.147-86.81c-22.938 13.304-49.482 21.047-77.853 21.047z"
                              // style=""
                              fill="#12b347"
                              data-original="#12b347"
                            ></path>
                            <path
                              d="M384 477.708v-.119l-50.147-86.81c-22.938 13.303-49.48 21.047-77.853 21.047V512c46.437 0 90.256-12.503 128-34.292z"
                              // style=""
                              fill="#0f993e"
                              data-original="#0f993e"
                            ></path>
                            <path
                              d="M100.174 256c0-28.369 7.742-54.91 21.043-77.847l-86.806-50.034C12.502 165.746 0 209.444 0 256s12.502 90.254 34.411 127.881l86.806-50.034c-13.301-22.937-21.043-49.478-21.043-77.847z"
                              // style=""
                              fill="#ffd500"
                              data-original="#ffd500"
                              // class=""
                            ></path>
                            <path
                              d="M256 100.174c37.531 0 72.005 13.336 98.932 35.519 6.643 5.472 16.298 5.077 22.383-1.008l47.27-47.27c6.904-6.904 6.412-18.205-.963-24.603C378.507 23.673 319.807 0 256 0 161.34 0 78.586 51.557 34.411 128.119l86.806 50.034c26.985-46.533 77.229-77.979 134.783-77.979z"
                              // style=""
                              fill="#ff4b26"
                              data-original="#ff4b26"
                            ></path>
                            <path
                              d="M354.932 135.693c6.643 5.472 16.299 5.077 22.383-1.008l47.27-47.27c6.903-6.904 6.411-18.205-.963-24.603C378.507 23.672 319.807 0 256 0v100.174c37.53 0 72.005 13.336 98.932 35.519z"
                              // style=""
                              fill="#d93f21"
                              data-original="#d93f21"
                            ></path>
                          </g>
                        </svg>
                        <span className="text-sm font-semibold leading-6 text-black">
                          Google
                        </span>
                      </a>

                      <a
                        onClick={() =>
                          window.location.assign(getGitHubUrl("github_login"))
                        }
                        className="cursor-pointer flex w-full items-center justify-center gap-3 rounded-md bg-[#24292F] px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F]"
                      >
                        <svg
                          className="h-5 w-5"
                          aria-hidden="true"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm font-semibold leading-6">
                          GitHub
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative hidden w-0 flex-1 lg:block h-screen">
              <img
                className="absolute inset-0 h-full w-full object-cover"
                src={bgImage}
              />
            </div>
          </div>
        )}

        {activeForm === "reset" && (
          <div className="flex min-h-full flex-1 max-lg:min-h-[80vh]">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
              <div className="mx-auto w-full max-w-sm lg:w-96">
                <div>
                  {/* <img
                className="h-10 w-auto"
                src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                alt="Your Company"
              /> */}
                  <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                    Reset Password
                  </h2>
                </div>

                <div className="mt-10">
                  <div>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        resetFormik.handleSubmit();
                      }}
                      className="space-y-6"
                    >
                      <div>
                        <label
                          htmlFor="userid"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          User ID
                        </label>
                        <div className="mt-2">
                          <input
                            id="userid"
                            name="userid"
                            type="userid"
                            autoComplete="userid"
                            required
                            className="border border-gray-300 block w-full rounded-md py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:ring-inset focus:border-0 focus:outline-indigo-600 sm:text-sm sm:leading-6 outline-none px-2"
                            onChange={resetFormik.handleChange}
                            value={resetFormik.values.userid}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Recovery E-mail
                        </label>
                        <div className="mt-2 relative">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            className="border border-gray-300 block w-full rounded-md py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:ring-inset focus:border-0 focus:outline-indigo-600 sm:text-sm sm:leading-6 outline-none px-2"
                            onChange={resetFormik.handleChange}
                            value={resetFormik.values.email}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm leading-6">
                          <a
                            onClick={() => setActiveForm("login")}
                            className="cursor-pointer font-semibold text-indigo-600 hover:text-indigo-500"
                          >
                            Go back to login
                          </a>
                        </div>
                      </div>

                      <div>
                        <button
                          disabled={loading}
                          type="submit"
                          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          {loading ? (
                            <svg
                              aria-hidden="true"
                              className="w-8 h-8 text-gray-200 animate-spin fill-blue-600"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                          ) : (
                            "Reset Password"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {activeForm === "login" && (
                    <div className="mt-10">
                      <div className="relative">
                        <div
                          className="absolute inset-0 flex items-center"
                          aria-hidden="true"
                        >
                          <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm font-medium leading-6">
                          <span className="bg-white px-6 text-gray-900">
                            Or continue with
                          </span>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-4">
                        <a
                          //   href="#"
                          onClick={() => loginWithGoogle()}
                          className="flex w-full items-center justify-center gap-3 rounded-md bg-white shadow-md border px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D9BF0] cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            version="1.1"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            width="512"
                            height="512"
                            x="0"
                            y="0"
                            viewBox="0 0 512 512"
                            // style="enable-background:new 0 0 512 512"
                            xmlSpace="preserve"
                            className="w-5 h-5"
                          >
                            <g>
                              <path
                                d="m492.668 211.489-208.84-.01c-9.222 0-16.697 7.474-16.697 16.696v66.715c0 9.22 7.475 16.696 16.696 16.696h117.606c-12.878 33.421-36.914 61.41-67.58 79.194L384 477.589c80.442-46.523 128-128.152 128-219.53 0-13.011-.959-22.312-2.877-32.785-1.458-7.957-8.366-13.785-16.455-13.785z"
                                // style=""
                                fill="#167ee6"
                                data-original="#167ee6"
                                // class=""
                              ></path>
                              <path
                                d="M256 411.826c-57.554 0-107.798-31.446-134.783-77.979l-86.806 50.034C78.586 460.443 161.34 512 256 512c46.437 0 90.254-12.503 128-34.292v-.119l-50.147-86.81c-22.938 13.304-49.482 21.047-77.853 21.047z"
                                // style=""
                                fill="#12b347"
                                data-original="#12b347"
                              ></path>
                              <path
                                d="M384 477.708v-.119l-50.147-86.81c-22.938 13.303-49.48 21.047-77.853 21.047V512c46.437 0 90.256-12.503 128-34.292z"
                                // style=""
                                fill="#0f993e"
                                data-original="#0f993e"
                              ></path>
                              <path
                                d="M100.174 256c0-28.369 7.742-54.91 21.043-77.847l-86.806-50.034C12.502 165.746 0 209.444 0 256s12.502 90.254 34.411 127.881l86.806-50.034c-13.301-22.937-21.043-49.478-21.043-77.847z"
                                // style=""
                                fill="#ffd500"
                                data-original="#ffd500"
                                // class=""
                              ></path>
                              <path
                                d="M256 100.174c37.531 0 72.005 13.336 98.932 35.519 6.643 5.472 16.298 5.077 22.383-1.008l47.27-47.27c6.904-6.904 6.412-18.205-.963-24.603C378.507 23.673 319.807 0 256 0 161.34 0 78.586 51.557 34.411 128.119l86.806 50.034c26.985-46.533 77.229-77.979 134.783-77.979z"
                                // style=""
                                fill="#ff4b26"
                                data-original="#ff4b26"
                              ></path>
                              <path
                                d="M354.932 135.693c6.643 5.472 16.299 5.077 22.383-1.008l47.27-47.27c6.903-6.904 6.411-18.205-.963-24.603C378.507 23.672 319.807 0 256 0v100.174c37.53 0 72.005 13.336 98.932 35.519z"
                                // style=""
                                fill="#d93f21"
                                data-original="#d93f21"
                              ></path>
                            </g>
                          </svg>
                          <span className="text-sm font-semibold leading-6 text-black">
                            Google
                          </span>
                        </a>

                        <a
                          onClick={() =>
                            window.location.assign(getGitHubUrl("github_login"))
                          }
                          className="cursor-pointer flex w-full items-center justify-center gap-3 rounded-md bg-[#24292F] px-3 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F]"
                        >
                          <svg
                            className="h-5 w-5"
                            aria-hidden="true"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-semibold leading-6">
                            GitHub
                          </span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="relative hidden w-0 flex-1 lg:block h-screen">
              <img
                className="absolute inset-0 h-full w-full object-cover"
                src={bgImage}
              />
            </div>
          </div>
        )}
      </ErrorBoundary>
    </>
  );
}

export default Login;

const randomImages = [
  "/assets/images/bangdb_login_signup_1.jpeg",
  "/assets/images/bangdb_login_signup_2.jpeg",
];
